import { useQuery } from "@tanstack/react-query";
import type { ReactNode } from "react";

import { getProjectRepository } from "../../data/projectRepository";
import { ContextHeader } from "./project-context";
import "./data-workspace.css";

export type DataCategoryKey =
  | "courses"
  | "rooms"
  | "teachers"
  | "classes"
  | "time_slots"
  | "unavailability";

export const DATA_CATEGORIES: ReadonlyArray<{
  key: DataCategoryKey;
  label: string;
  segment: string;
}> = [
  { key: "courses", label: "课程信息", segment: "courses" },
  { key: "classes", label: "班级信息", segment: "classes" },
  { key: "teachers", label: "教师信息", segment: "teachers" },
  { key: "rooms", label: "功能教室信息", segment: "rooms" },
  { key: "time_slots", label: "课程表时间", segment: "time_slots" },
  { key: "unavailability", label: "不排课时间", segment: "unavailability" }
];

interface DataWorkspaceProps {
  projectId: string;
  /** 当前类别，同时决定导航高亮与页面标题 */
  active: DataCategoryKey;
  description?: string;
  children: ReactNode;
}

/**
 * A 方向「资料准备工作区」共享外壳：
 * ContextHeader（返回课表主页 + 项目名 + 当前类别名）
 * + 左侧六类持久导航（.data-nav）+ 中央数据主区（.data-main）。
 * 各基础信息页只提供数据主体（工具栏、列表、编辑器）。
 */
export function DataWorkspace({
  projectId,
  active,
  description,
  children
}: DataWorkspaceProps): React.JSX.Element {
  const project = useQuery({
    queryKey: ["project", projectId],
    queryFn: async () => (await getProjectRepository()).read(projectId)
  });
  const activeCategory = DATA_CATEGORIES.find(
    (category) => category.key === active
  );
  return (
    <main className="page-shell page-shell-wide">
      <ContextHeader
        projectId={projectId}
        title={activeCategory?.label ?? "基础信息"}
        description={description}
        backTo={{ href: `/projects/${projectId}`, label: "返回课表主页" }}
      />
      <div className="data-layout">
        <nav className="data-nav" aria-label="基础信息类别">
          {DATA_CATEGORIES.map((category) => {
            const isActive = category.key === active;
            const count = categoryCount(project.data?.module_counts, category.key);
            return (
              <a
                key={category.key}
                className={isActive ? "active" : undefined}
                href={`/projects/${projectId}/${category.segment}`}
                aria-current={isActive ? "page" : undefined}
              >
                <span>{category.label}</span>
                <span className="pill" aria-label={`${category.label}数量`}>
                  {typeof count === "number" ? count : "…"}
                </span>
              </a>
            );
          })}
        </nav>
        <div className="data-main">
          {project.isError ? (
            <p className="error-message" role="alert">
              无法读取基础资料数量，请刷新后重试。
            </p>
          ) : null}
          {children}
        </div>
      </div>
    </main>
  );
}

function categoryCount(
  counts: Record<string, number> | undefined,
  category: DataCategoryKey
): number | undefined {
  if (!counts) return undefined;
  const key = category === "rooms" ? "room_types" : category;
  return counts[key];
}
